// src/features/audittrail/RecordDetailRail.tsx
// One audit record: hash chain fields, actor, before/after state, metadata.

import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { AuditRecord } from '@core/types/audittrail';
import { enumKey, fmtDateTime } from './logic';

interface RecordDetailRailProps {
  record: AuditRecord;
  onClose: () => void;
}

export const RecordDetailRail = observer(({ record, onClose }: RecordDetailRailProps) => {
  const { t, locale } = useI18n();
  const metadata = Object.entries(record.metadata ?? {});

  return (
    <aside className="w-full shrink-0 space-y-3 overflow-y-auto border-l border-border p-4 lg:w-[28rem]">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('audittrail.records.detail.title')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      <div>
        <p className="text-sm font-medium text-foreground">{record.event_type}</p>
        <p className="mt-0.5 font-mono text-xs text-muted-foreground break-all">{record.resource_urn}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Badge size="sm">{t(`audittrail.auditAction.${enumKey(record.action, 'AUDIT_ACTION_')}`)}</Badge>
          <Badge variant="secondary" size="sm">{t(`audittrail.dataClassification.${enumKey(record.data_classification, 'DATA_CLASSIFICATION_')}`)}</Badge>
        </div>
      </div>

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('audittrail.records.detail.actor')}</dt>
          <dd className="text-right text-foreground">{record.actor?.display_name || record.actor?.entity?.id || '—'}</dd>
        </div>
        {record.actor_ip && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('audittrail.records.detail.actorIp')}</dt>
            <dd className="text-right text-foreground">{record.actor_ip}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('audittrail.records.detail.occurred')}</dt>
          <dd className="text-right text-foreground">{fmtDateTime(record.occurred_at as unknown as string, locale)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{t('audittrail.records.detail.sequence')}</dt>
          <dd className="text-right text-foreground">{record.sequence_number}</dd>
        </div>
        {record.justification && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">{t('audittrail.records.detail.justification')}</dt>
            <dd className="text-right text-foreground">{record.justification}</dd>
          </div>
        )}
      </dl>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('audittrail.records.detail.hashChain')}</h3>
        <dl className="mt-1 space-y-1 text-xs">
          <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{t('audittrail.records.detail.recordHash')}</dt><dd className="text-right text-foreground break-all font-mono">{record.record_hash || '—'}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{t('audittrail.records.detail.previousHash')}</dt><dd className="text-right text-foreground break-all font-mono">{record.previous_hash || '—'}</dd></div>
        </dl>
      </div>

      {(record.before_state || record.after_state) && (
        <div className="space-y-2">
          {record.before_state && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('audittrail.records.detail.before')}</h3>
              <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs">{record.before_state}</pre>
            </div>
          )}
          {record.after_state && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('audittrail.records.detail.after')}</h3>
              <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-muted p-2 text-xs">{record.after_state}</pre>
            </div>
          )}
        </div>
      )}

      {metadata.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('audittrail.records.detail.metadata')}</h3>
          <dl className="mt-1 space-y-1 text-sm">
            {metadata.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right text-foreground break-all">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </aside>
  );
});

export default RecordDetailRail;
